import { supabase } from '../lib/supabaseClient';

// ============================================================================
// 📧 EMAIL SERVICE - Frontend Wrapper
// ============================================================================
// Este serviço chama a Supabase Edge Function 'send-email' que roda no servidor
// A Edge Function usa o Resend para enviar emails (API key segura no servidor)
// ============================================================================

interface SendWelcomeEmailParams {
  toEmail: string;
  organizationName: string;
  planName: string;
  maxUsers: number;
  trialDays: number;
  invitationToken: string;
  valorMensal?: number;
}

interface SendTeamInviteParams {
  toEmail: string;
  inviterName: string;
  organizationName: string;
  role: string;
  invitationToken: string;
}

/**
 * Gera um token único e seguro para convites usando Web Crypto API
 */
export function generateInvitationToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Envia email de boas-vindas para administrador do cliente via Edge Function
 */
export async function sendWelcomeEmail(params: SendWelcomeEmailParams): Promise<{ success: boolean; error?: string }> {
  try {
    const activationLink = `${window.location.origin}/#/activate?token=${params.invitationToken}`;
    
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
    const EMAIL_SECRET = import.meta.env.VITE_EMAIL_FUNCTION_SECRET || '';
    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(`${SUPABASE_URL}/functions/v1/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`,
        'x-email-secret': EMAIL_SECRET,
      },
      body: JSON.stringify({
        type: 'welcome',
        to: params.toEmail,
        data: {
          nome: params.organizationName,
          organizationName: params.organizationName,
          activationLink,
          plano: params.planName,
          trial_days: params.trialDays,
          max_usuarios: params.maxUsers,
          valor_mensal: params.valorMensal,
        }
      })
    });

    console.log('📡 Response status:', response.status, response.statusText);
    
    const data = await response.json();
    const error = response.ok ? null : data;

    if (error) {
      console.warn('⚠️ Edge Function não disponível (desenvolvimento local)');
      console.log('💡 Para enviar emails reais, siga o guia: DEPLOY_GUIDE_EMAIL.md');
      console.log('📧 Email simulado enviado para:', params.toEmail);
      
      // Em desenvolvimento, retornar sucesso simulado
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return { 
          success: true, 
          error: 'Email simulado (Edge Function não deployada). Veja console para detalhes do convite.' 
        };
      }
      
      return { success: false, error: error.message };
    }

    console.log('✅ Email enviado via Edge Function:', data);
    return { success: true };

  } catch (error: any) {
    console.warn('⚠️ Erro ao chamar Edge Function:', error.message);
    console.log('💡 Para enviar emails reais, deploy a Edge Function: supabase functions deploy send-email');
    
    // Em desenvolvimento, retornar sucesso simulado
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('📧 Email simulado para:', params.toEmail);
      console.log('🔗 Link de ativação:', `${window.location.origin}/activate?token=${params.invitationToken}`);
      return { 
        success: true, 
        error: 'Email simulado (desenvolvimento local). Veja console para detalhes.' 
      };
    }
    
    return { success: false, error: error.message || 'Erro desconhecido' };
  }
}

/**
 * Envia email de convite para membro da equipe via Edge Function
 */
export async function sendTeamInviteEmail(params: SendTeamInviteParams): Promise<{ success: boolean; error?: string }> {
  try {
    const activationLink = `${window.location.origin}/#/invite?token=${params.invitationToken}`;
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
    const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
    const EMAIL_SECRET = import.meta.env.VITE_EMAIL_FUNCTION_SECRET || '';

    const response = await fetch(`${SUPABASE_URL}/functions/v1/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANON_KEY}`,
        'x-email-secret': EMAIL_SECRET,
      },
      body: JSON.stringify({
        type: 'team-invite',
        to: params.toEmail,
        data: {
          inviterName: params.inviterName,
          organizationName: params.organizationName,
          activationLink,
          role: params.role,
        },
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      console.warn('⚠️ Erro ao enviar email via Edge Function:', data);
      return { success: false, error: data?.error || 'Erro ao enviar email' };
    }

    console.log('✅ Convite enviado via Edge Function:', data);
    return { success: true };

  } catch (error: any) {
    console.warn('⚠️ Erro ao chamar Edge Function:', error.message);
    return { success: false, error: error.message || 'Erro desconhecido' };
  }
}

// ============================================================================
// 📧 ADDITIONAL TRANSACTIONAL EMAILS
// ============================================================================

interface SendSlaAlertParams {
  toEmail: string;
  userName: string;
  workflowTitle: string;
  stageName: string;
  referenceCode: string;
  slaHoursRemaining: number;
  instanceUrl: string;
}

interface SendSubscriptionAlertParams {
  toEmail: string;
  organizationName: string;
  planName: string;
  eventType: 'trial_expiring' | 'payment_failed' | 'subscription_renewed' | 'plan_upgraded';
  daysRemaining?: number;
  amount?: number;
}

/**
 * Helper: calls the send-email Edge Function with given type + data
 */
async function callEmailFunction(
  type: string,
  to: string,
  data: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> {
  const isLocal = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  try {
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
    const EMAIL_SECRET = import.meta.env.VITE_EMAIL_FUNCTION_SECRET || '';
    const { data: { session } } = await supabase.auth.getSession();

    const response = await fetch(`${SUPABASE_URL}/functions/v1/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`,
        'x-email-secret': EMAIL_SECRET,
      },
      body: JSON.stringify({ type, to, data }),
    });

    if (!response.ok) {
      if (isLocal) {
        console.log(`📧 [${type}] Email simulado para: ${to}`, data);
        return { success: true, error: 'Email simulado (desenvolvimento local).' };
      }
      const err = await response.json();
      return { success: false, error: err.message || 'Falha no envio' };
    }

    return { success: true };
  } catch (err: any) {
    if (isLocal) {
      console.log(`📧 [${type}] Email simulado para: ${to}`, data);
      return { success: true, error: 'Email simulado (desenvolvimento local).' };
    }
    return { success: false, error: err.message };
  }
}

/**
 * Sends SLA warning email when a task is approaching its deadline.
 */
export async function sendSlaAlertEmail(params: SendSlaAlertParams) {
  return callEmailFunction('sla-alert', params.toEmail, {
    userName: params.userName,
    workflowTitle: params.workflowTitle,
    stageName: params.stageName,
    referenceCode: params.referenceCode,
    slaHoursRemaining: params.slaHoursRemaining,
    instanceUrl: params.instanceUrl,
  });
}

/**
 * Sends subscription lifecycle emails (trial expiring, payment failed, etc.)
 */
export async function sendSubscriptionAlertEmail(params: SendSubscriptionAlertParams) {
  return callEmailFunction('subscription-alert', params.toEmail, {
    organizationName: params.organizationName,
    planName: params.planName,
    eventType: params.eventType,
    daysRemaining: params.daysRemaining,
    amount: params.amount,
  });
}

/**
 * Sends workflow completion summary to the initiator.
 */
export async function sendWorkflowCompletedEmail(
  toEmail: string,
  data: { userName: string; workflowTitle: string; referenceCode: string; completedAt: string; totalHours: number }
) {
  return callEmailFunction('workflow-completed', toEmail, data);
}

/**
 * Sends kaizen regression alert to the responsible.
 */
export async function sendKaizenRegressionEmail(
  toEmail: string,
  data: { kaizenTitle: string; metricName: string; currentValue: number; targetValue: number; deviation: number }
) {
  return callEmailFunction('kaizen-regression', toEmail, data);
}

export async function sendActionAssignedEmail(
  toEmail: string,
  data: { userName: string; actionTitle: string; projectName: string; deadline?: string; actionUrl: string }
) {
  return callEmailFunction('action-assigned', toEmail, data);
}
